import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { haSessione, letturaPubblica, type Servizio } from '@/lib/ospite';

/**
 * Il testo dell'errore che una richiesta bloccata restituisce. Esplicito, come quello di
 * `socialApi` a flag spento: chi lo legge deve capire che e' una scelta di build, non la rete.
 */
export const ERRORE_OSPITE = 'ospite: questa richiesta chiede un account (VITE_FEATURE_GUEST_ENABLED)';

/**
 * 🔴 IL CANCELLO DI RETE DELL'OSPITE — uno per client, e sta QUI e non nelle schermate.
 *
 * Finche' `AppShell` rendeva `<Login />` al posto delle rotte, nessuna schermata aveva bisogno
 * di una guardia propria: senza sessione non se ne montava nessuna. Aprire le rotte all'ospite
 * rende raggiungibile OGNI query e ogni mutation; mettere una guardia in ogni schermata
 * vorrebbe dire contarle tutte, e dimenticarne una. Qui ce n'e' una per client, e il default e'
 * il NO: da ospite esce solo cio' che `letturaPubblica` (`lib/ospite.ts`) elenca.
 *
 * ⇒ Garanzie (provate in `tests/ospite-non-fa-rete.test.ts`):
 *  · da ospite nessuna mutation esce, e nessuna lettura di un servizio che chiede un token;
 *  · la richiesta bloccata NON tocca la rete: torna un `CUSTOM_ERROR`, subito;
 *  · con una sessione, o a interruttore spento, la guardia non esiste: passa tutto, identico.
 *
 * «Ospite» qui = `!haSessione(stato)` (`lib/ospite.ts`): bridge O SDK, lo STESSO predicato
 * di `useOspite` e quindi di `AppShell`. Schermata e rete non possono dare due risposte diverse
 * sulla stessa persona.
 * ⚠️ Sta FUORI da `retry(...)`, di proposito: una richiesta bloccata non va ritentata.
 */
export function guardiaOspite<BQ extends BaseQueryFn>(servizio: Servizio, baseQuery: BQ): BQ {
  const guardato = (async (args, api, extraOptions) => {
    if (env.featureGuestEnabled && !haSessione(api.getState())) {
      if (!letturaPubblica(servizio, args as Parameters<typeof letturaPubblica>[1])) {
        return { error: { status: 'CUSTOM_ERROR', error: ERRORE_OSPITE } };
      }
    }
    return baseQuery(args, api, extraOptions);
  }) as BaseQueryFn as unknown as BQ;
  return guardato;
}

